import cx from 'classnames'
import * as _ from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Box, Button, Typography } from '@mui/material'
import {
  getAllModulesSubgroups,
  updateCourseClassesById,
} from '@/store/knowledge/actions'
import Toggle from '@/components/UI/Toggle'
import { HelperService } from '@/services/helper.service'
import '@/routes/Knowledge/ManageAccessRights/ManageAccessRights.scss'

const helperService = new HelperService()

interface ClassAccessRightsProps {
  classId: string
  initialAccessAllKnowledge?: boolean
  initialSelectedGroupIds?: string[]
  onUpdate?: () => void
}

const ClassAccessRights = ({
  classId,
  initialAccessAllKnowledge = false,
  initialSelectedGroupIds = [],
  onUpdate,
}: ClassAccessRightsProps) => {
  const { t } = useTranslation('translation')
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [allGroups, setAllGroups] = useState([])
  const [selectedGroupId, setSelectedGroupId] = useState<string[]>(
    initialSelectedGroupIds,
  )
  const [accessAllKnowledge, setAccessAllKnowledge] = useState(
    initialAccessAllKnowledge,
  )

  useEffect(() => {
    getAllGroups()
  }, [])

  useEffect(() => {
    setSelectedGroupId(initialSelectedGroupIds)
    setAccessAllKnowledge(initialAccessAllKnowledge)
  }, [initialSelectedGroupIds, initialAccessAllKnowledge])

  const getAllGroups = async () => {
    try {
      const response: any = await dispatch(getAllModulesSubgroups())
      setAllGroups(
        Array.isArray(response)
          ? response.filter((g: any) => g?.group_type !== 'system')
          : [],
      )
    } catch (error: any) {
      setAllGroups([])
    }
  }

  const handleUpdate = async () => {
    setLoading(true)
    try {
      await dispatch(
        updateCourseClassesById(classId, selectedGroupId, accessAllKnowledge),
      )
      helperService.showToaster(
        t('Access rights updated successfully.'),
        'success',
      )
      onUpdate && onUpdate()
    } catch (error: any) {
      helperService.showToaster(t('Failed to update access rights.'), 'error')
    } finally {
      setLoading(false)
    }
  }

  const chooseCategoryHandler = (e: any) => {
    const isPresentIndex = selectedGroupId.indexOf(e.target.value)
    const oldSelectedOptions = [...selectedGroupId]
    const childrensArray: string[] = []
    const func = (value: string) => {
      const childrens = allGroups.filter((p: any) => p.parent_id === value)
      if (childrens.length) {
        childrens.forEach(d => {
          func(d.value)
        })
      }
      childrensArray.push(value)
    }
    func(e.target.value)

    if (e.target.checked && isPresentIndex === -1) {
      oldSelectedOptions.push(e.target.value)
      return setSelectedGroupId(
        _.uniq(oldSelectedOptions.concat(childrensArray)),
      )
    }

    if (!e.target.checked && isPresentIndex >= 0) {
      oldSelectedOptions.splice(isPresentIndex, 1)
      childrensArray.forEach((id: string) => {
        const isPresentIndex = _.uniq(oldSelectedOptions).indexOf(id)
        if (isPresentIndex >= 0) {
          oldSelectedOptions.splice(isPresentIndex, 1)
        }
      })
      return setSelectedGroupId(_.uniq(oldSelectedOptions))
    }
  }

  const accessAllKnowledgeChangedHandler = (e: any) => {
    setAccessAllKnowledge(e.target.checked)
  }

  return (
    <section
      className={cx({
        'user-groups pb-0': true,
        hidden: loading,
      })}
    >
      <div>
        <div className="d-flex justify-content-end mb-3">
          <Button
            variant="contained"
            size="medium"
            disabled={loading}
            onClick={() => handleUpdate()}
          >
            {t('Update')}
          </Button>
        </div>

        <div className="pref-check mt-4 notify-onOff switch-blue">
          <Toggle
            toggled={accessAllKnowledge}
            onToggle={accessAllKnowledgeChangedHandler}
            label={t('Allow Access to all knowledge?')}
            InputPosition="end"
          />
        </div>

        <Box className="modulesContainer">
          <div className="header">
            <Typography variant="h3">{t('Groups')}</Typography>
            <Typography variant="h3">{t('Can Access')}</Typography>
          </div>
          <div className="modules-list flex-grow-1">
            <ul className="">
              {allGroups &&
                allGroups.map((mod: any) => {
                  return (
                    <li
                      key={'child_padding_dev_' + mod.value}
                      style={{
                        paddingLeft: `${
                          mod.depth > 0 ? mod.depth * 10 + 10 : 10
                        }px`,
                      }}
                      className={cx({
                        'clr-grey-txt': mod.depth > 0,
                        disabled:
                          accessAllKnowledge ||
                          selectedGroupId.indexOf(mod.parent_id) >= 0,
                      })}
                    >
                      <div className="moduleContainer">
                        <h3>{mod.label}</h3>
                        <input
                          type="checkbox"
                          value={mod.value}
                          disabled={
                            accessAllKnowledge ||
                            selectedGroupId.indexOf(mod.parent_id) >= 0
                          }
                          checked={
                            selectedGroupId.indexOf(mod.value) >= 0 ||
                            accessAllKnowledge ||
                            selectedGroupId.indexOf(mod.parent_id) >= 0
                          }
                          onChange={chooseCategoryHandler}
                        />
                      </div>
                    </li>
                  )
                })}
            </ul>
          </div>
        </Box>
      </div>
    </section>
  )
}

export default ClassAccessRights
